/**
 * Card List With Dropdown Block JavaScript
 *
 * @param {boolean} [enableDebug=false] - Enable debug logging
 */
export default function (enableDebug = false) {
	const instances = document.querySelectorAll('.card-list-with-dropdown');

	if (enableDebug) console.log(`[CardListDropdown] Found ${instances.length} instance(s)`);
	if (instances.length === 0) return;

	instances.forEach(function (instance, instanceIndex) {
		if (enableDebug) console.log(`[CardListDropdown] Initializing instance ${instanceIndex + 1}`);

		const blockName = 'card-list-with-dropdown';
		const cardsWrapper = instance.querySelector(`.${blockName}__cards-list`);
		const cards = Array.prototype.slice.call(instance.querySelectorAll(`.${blockName}__card`));
		const nameSortSelect = instance.querySelector('.--flname');
		const yearSortSelect = instance.querySelector('.--year');
		const orderSortSelect = instance.querySelector('.--sorter select');
		const searchForm = instance.querySelector('.cards-list-search-form');
		const searchInput = instance.querySelector('#cards-list-search-input');
		const paginationWrapper = instance.querySelector(`.${blockName}__pagination`);
		const paginationList = instance.querySelector(`.${blockName}__pagination-list`);

		// Defensive check: if no cards wrapper exists, skip initialization
		if (!cardsWrapper) {
			if (enableDebug) console.log(`[CardListDropdown] No cards wrapper found for instance ${instanceIndex + 1}, skipping initialization`);
			return;
		}

		// Defensive check: if no cards exist, skip initialization
		if (cards.length === 0) {
			if (enableDebug) console.log(`[CardListDropdown] No cards found for instance ${instanceIndex + 1}, skipping initialization`);
			return;
		}

		const pagedLimit = parseInt(cardsWrapper.getAttribute('data-cards-paged'), 10) || cards.length;

		let currentPage = 1;
		let currentFilteredCards = [];

		let activeYear = '';
		let activeNameSort = '';
		let activeSearch = '';
		let activeOrderSort = '';

		const cardData = cards.map(function (card) {
			return {
				element: card,
				firstname: (card.getAttribute('data-card-firstname') || '').toLowerCase(),
				lastname: (card.getAttribute('data-card-lastname') || '').toLowerCase(),
				year: card.getAttribute('data-card-year') || '',
				index: parseInt(card.getAttribute('data-card-index'), 10) || 0,
				shortBio: (card.querySelector(`.${blockName}__card-short-bio`) || {}).textContent || '',
			};
		});

		function getFilteredCards() {
			let result = cardData.slice();

			if (activeYear) {
				result = result.filter(function (card) {
					return card.year && card.year.trim() === activeYear;
				});
			}

			if (activeSearch) {
				const kw = activeSearch.toLowerCase();
				result = result.filter(function (card) {
					return (
						card.firstname.includes(kw) ||
						card.lastname.includes(kw) ||
						card.shortBio.toLowerCase().includes(kw)
					);
				});
			}

			if (activeNameSort === '1') {
				result.sort(function (a, b) {
					return a.firstname.localeCompare(b.firstname);
				});
			} else if (activeNameSort === '2') {
				result.sort(function (a, b) {
					return a.lastname.localeCompare(b.lastname);
				});
			}

			if (activeOrderSort === '1') {
				result.sort(function (a, b) {
					return b.index - a.index;
				});
			} else if (activeOrderSort === '2') {
				result.sort(function (a, b) {
					return a.index - b.index;
				});
			}

			if (activeYear || activeSearch || activeNameSort === '1' || activeNameSort === '2' || activeOrderSort === '1' || activeOrderSort === '2') {
				const reset = instance.querySelector('.card-list-with-dropdown--reset-filters');
				if (reset) {
					reset.classList.add('active-filters');
				}
			}

			if (enableDebug) console.log(`[CardListDropdown] Final filtered ${result.length} cards`);
			return result;
		}

		function applyPaging(filteredCards) {
			cardData.forEach(function (card) {
				card.element.classList.add('--hidden');
			});

			const start = (currentPage - 1) * pagedLimit;
			const end = start + pagedLimit;

			filteredCards.forEach(function (card, index) {
				if (index >= start && index < end) {
					card.element.classList.remove('--hidden');
				}
			});
		}

		function renderPagination(filteredCards) {
			if (!paginationWrapper || !paginationList) return;

			paginationList.innerHTML = '';
			const totalPages = Math.ceil(filteredCards.length / pagedLimit);

			if (totalPages <= 1) {
				paginationWrapper.style.display = 'none';
				return;
			}

			paginationWrapper.style.display = '';
			const createPageItem = function (label, page, isActive, isDisabled, customClass) {
				const li = document.createElement('li');
				li.className = `${blockName}__pagination-list-item`;
				if (isActive) li.classList.add('--active');
				if (isDisabled) li.classList.add('--disabled');

				const a = document.createElement('a');
				a.href = '#';
				a.className = `${blockName}__pagination-link`;
				if (customClass) a.classList.add(customClass);
				if (!['--start-page', '--previous-page', '--next-page', '--end-page'].includes(customClass)) {
					a.textContent = label;
				}

				if (!isDisabled) {
					a.addEventListener('click', function (e) {
						e.preventDefault();
						currentPage = page;
						applyPaging(filteredCards);
						renderPagination(filteredCards);

						// Scroll to top of instance
						instance.scrollIntoView({ behavior: 'smooth', block: 'start' });
					});
				}

				li.appendChild(a);
				paginationList.appendChild(li);
			};

			createPageItem('', 1, false, currentPage === 1, '--start-page');
			createPageItem('', currentPage - 1, false, currentPage === 1, '--previous-page');

			for (let i = 1; i <= totalPages; i++) {
				if (i === 1 || i === totalPages || (i >= currentPage - 2 && i <= currentPage + 2)) {
					createPageItem(i.toString(), i, currentPage === i, false, null);
				} else if (i === currentPage - 3 || i === currentPage + 3) {
					const li = document.createElement('li');
					li.className = `${blockName}__pagination-list-item`;
					li.innerHTML = '…';
					paginationList.appendChild(li);
				}
			}

			createPageItem('', currentPage + 1, false, currentPage === totalPages, '--next-page');
			createPageItem('', totalPages, false, currentPage === totalPages, '--end-page');
		}

		function updateResults(filteredCards) {
			currentFilteredCards = filteredCards;
			currentPage = 1;

			filteredCards.forEach(function (card) {
				cardsWrapper.appendChild(card.element);
			});

			applyPaging(filteredCards);
			renderPagination(filteredCards);
		}

		updateResults(getFilteredCards());

		if (nameSortSelect) {
			nameSortSelect.addEventListener('change', function (e) {
				activeNameSort = e.target.value;
				if (enableDebug) console.log(`[CardListDropdown] Name sort changed: ${activeNameSort}`);
				updateResults(getFilteredCards());
			});
		}

		if (yearSortSelect) {
			const seenYears = {};
			const uniqueYears = [];

			cardData.forEach(function (card) {
				const y = card.year;
				if (y && !seenYears[y]) {
					seenYears[y] = true;
					uniqueYears.push(y);
				}
			});

			uniqueYears.sort(function (a, b) {
				const aNum = parseInt(a, 10);
				const bNum = parseInt(b, 10);
				if (!isNaN(aNum) && !isNaN(bNum)) return bNum - aNum;
				if (!isNaN(aNum)) return -1;
				if (!isNaN(bNum)) return 1;
				return a.localeCompare(b);
			});

			uniqueYears.forEach(function (year) {
				const opt = document.createElement('option');
				opt.value = year;
				opt.textContent = year;
				if (yearSortSelect) {
					yearSortSelect.appendChild(opt);
				}
			});

			if (yearSortSelect) {
				yearSortSelect.addEventListener('change', function (e) {
					activeYear = (e.target.value || '').trim();
					if (enableDebug) console.log(`[CardListDropdown] Year filter changed: "${activeYear}"`);
					updateResults(getFilteredCards());
				});
			}
		}

		if (orderSortSelect) {
			orderSortSelect.addEventListener('change', function (e) {
				activeOrderSort = e.target.value;
				if (enableDebug) console.log(`[CardListDropdown] Order sort changed: ${activeOrderSort}`);
				updateResults(getFilteredCards());
			});
		}

		if (searchForm && searchInput) {
			searchForm.addEventListener('submit', function (e) {
				e.preventDefault();
				activeSearch = searchInput.value.trim().toLowerCase();
				if (enableDebug) console.log(`[CardListDropdown] Search submitted: "${activeSearch}"`);
				updateResults(getFilteredCards());
			});
		}

		const resetFiltersButton = instance.querySelector('.card-list-with-dropdown--reset-filters');
		if (resetFiltersButton) {
			resetFiltersButton.addEventListener('click', function (e) {
				e.preventDefault();
				activeYear = '';
				activeNameSort = '';
				activeOrderSort = '';
				activeSearch = '';
				if (nameSortSelect) nameSortSelect.value = '';
				if (yearSortSelect) yearSortSelect.value = '';
				if (orderSortSelect) orderSortSelect.value = '';
				if (searchInput) searchInput.value = '';
				if (resetFiltersButton) resetFiltersButton.classList.remove('active-filters');
				updateResults(getFilteredCards());
				if (enableDebug) console.log(`[CardListDropdown] Filters reset`);
			});
		}

		cards.forEach(function (card) {
			const toggleButton = card.querySelector(`.${blockName}__card-toggle`);
			if (!toggleButton) return;

			const contentId = toggleButton.getAttribute('aria-controls');
			const contentDiv = card.querySelector('#' + contentId);
			if (!contentDiv) return;

			contentDiv.setAttribute('aria-hidden', 'true');

			toggleButton.addEventListener('click', function () {
				const isExpanded = toggleButton.classList.contains('--expanded');
				toggleButton.classList.toggle('--expanded', !isExpanded);
				contentDiv.classList.toggle('--expanded', !isExpanded);
				toggleButton.setAttribute('aria-expanded', String(!isExpanded));
				contentDiv.setAttribute('aria-hidden', String(isExpanded));

				// NEW: Toggle button text swap based on data attributes
				const moreText = toggleButton.getAttribute('data-text-more');
				const lessText = toggleButton.getAttribute('data-text-less');
				if (lessText && moreText) {
					toggleButton.innerText = isExpanded ? moreText : lessText;
				}
			});
		});
	});
}
